import * as Sentry from '@sentry/core'
import { getUploadData } from './util'
import type { CsvOwner } from './uploadFile'
import http from '@paintscout/util/http'
import putFileViaXhr from './putFileViaXhr'

export interface UploadOptions {
  /**
   * If provided(better be), folder will be setup accordingly /companyId/x
   */
  companyId: string

  /**
   * Temporary feature flag on s3 uploads
   */
  s3UploadFeatureEnabled: boolean

  /**
   * LaunchDarkly flag `s3MediaUpload`: when true, new PDFs/videos are routed to
   * S3 instead of being force-routed to Cloudinary. Off (the default) keeps the
   * existing Cloudinary behaviour so it can be rolled out/back per-cohort.
   */
  s3MediaUploadEnabled?: boolean

  /**
   * If provided, a tag of `doc-<id>` will be categorized under /companyId/doc-id/'x'
   * x = /{id} | /images/{id}
   */
  docId?: string

  /**
   * Type of csv upload we are doing
   */
  type?: string

  /**
   * Owner of newly created quote/contacts on csv upload
   */
  owner?: CsvOwner

  /**
   * Template used for upload
   */
  provider?: string

  tags?: string[]
}

export interface S3FileUploadData {
  size: number
  src: string
  s3PublicKey: string
  bucket: string
  format: string
  type: string
  pages?: number
  width?: number
  height?: number
}

export interface S3UrlComponents {
  key: string
  rawUrl: string
  bucket: string
}

// Cap the S3 PUT so a stalled upload rejects instead of hanging forever. A hung
// PUT (e.g. flaky tablet connection on iPad) otherwise leaves the file's
// `isUploading` flag stuck and permanently disables the "Done" button (#21104).
const S3_UPLOAD_TIMEOUT_MS = 60000

/**
 * Upload file to correct key in images bucket
 */
export default async function s3UploadFile(file: File, url: string): Promise<S3FileUploadData> {
  // The canned ACL is already baked into the presigned URL's signed query string
  // (see generate-signed-url.ts), so we only need Content-Type on the PUT. We
  // deliberately do NOT send an `ACL` header: it's not the header S3 reads
  // (`x-amz-acl` is), so it was a no-op, and dropping it trims the CORS preflight.
  const headers = { 'Content-Type': file.type }

  try {
    // Browser: PUT via XMLHttpRequest. Native fetch (used by the shared `http`
    // client since #20385) rejects large cross-origin Blob PUTs with "Load
    // failed" on iOS/iPadOS WebKit (#21104); XHR is the proven browser path.
    // Server/Node has no XMLHttpRequest, so fall back to the fetch client there.
    if (typeof XMLHttpRequest !== 'undefined') {
      await putFileViaXhr({ url, file, headers, timeout: S3_UPLOAD_TIMEOUT_MS })
    } else {
      await http({ method: 'PUT', url, timeout: S3_UPLOAD_TIMEOUT_MS, headers, data: file })
    }
  } catch (error) {
    // Log file metadata only — the presigned `url` carries AWS signature
    // credentials in its query string and must never reach the console.
    console.log('error uploading file to s3', { name: file.name, type: file.type, error })
    Sentry.captureException(error)
    // Re-throw so callers don't proceed to trigger downstream processing
    // (e.g. the CSV handler lambda) against a file that never landed in S3.
    throw error
  }

  const uploadData = await getUploadData(file, url)

  return uploadData
}
